import dayjs from "dayjs";

const DATE_FORMAT = "YYYY-MM-DD";

export function getDateRangeForTimeframe(timeframe: string): {
  startDate: string;
  endDate: string;
} {
  const today = dayjs();

  switch (timeframe) {
    case "two_days_ago":
      return {
        startDate: today.subtract(2, "day").format(DATE_FORMAT),
        endDate: today.subtract(2, "day").format(DATE_FORMAT),
      };

    case "three_days_ago":
      return {
        startDate: today.subtract(3, "day").format(DATE_FORMAT),
        endDate: today.subtract(3, "day").format(DATE_FORMAT),
      };

    case "mtd":
      return {
        startDate: today.startOf("month").format(DATE_FORMAT),
        endDate: today.format(DATE_FORMAT),
      };

    case "yesterday":
    default:
      return {
        startDate: today.subtract(1, "day").format(DATE_FORMAT),
        endDate: today.subtract(1, "day").format(DATE_FORMAT),
      };
  }
}

// ─── Table constants ────────────────────────────────────────────────────────

export const PERC_SUFFIX = "_perc";

// Columns that should only appear when timeframe is MTD
export const MTD_ONLY_KEYS = new Set(["ftd_incent", "ftd_incent_perc"]);

export const NO_COMPARISON_KEYS = new Set([
  "brand_display_name",
  "brand_id",
  "geo",
]);

// Frontend-computed percentage for columns where _perc is not provided by API
export const COMPUTED_PERC: Record<
  string,
  (row: Record<string, any>) => number | null
> = {
  cpa_income_usd: (row) => {
    const total = Number(row.total_income_usd);
    const val = Number(row.cpa_income_usd);
    return total > 0 ? (val / total) * 100 : null;
  },
  revshare_income_usd: (row) => {
    const total = Number(row.total_income_usd);
    const val = Number(row.revshare_income_usd);
    return total > 0 ? (val / total) * 100 : null;
  },
};

// ─── Badge styles ────────────────────────────────────────────────────────────

export const RED = { bg: "#FDEDED", color: "#D32F2F", border: "#D32F2F80" };
export const YELLOW = { bg: "#FFFAEB", color: "#DC6803", border: "#ffc593" };
export const GREEN = { bg: "#EDF7ED", color: "#2E7D32", border: "#2E7D3280" };

export const getBadgeStyle = (
  percKey: string,
  percent: number,
  isMtd: boolean,
) => {
  // QFTD — MTD only: high is bad (inverted scale)
  if (percKey === "qftd_perc" && isMtd) {
    if (percent >= 20) return RED;
    if (percent > 10) return YELLOW;
    return GREEN;
  }
  // QFTD Incent — MTD only: same inverted scale as QFTD
  if (percKey === "ftd_incent" && isMtd) {
    if (percent >= 20) return RED;
    if (percent > 10) return YELLOW;
    return GREEN;
  }
  // Incent & Tier 5 — MTD only: two-colour scale
  if (percKey === "ftd_incent_perc" && isMtd) {
    return percent >= 25 ? RED : GREEN;
  }
  // FTD and all other _perc fields (including QFTD non-MTD)
  // Spec: ≤12 red | 12<x<20 yellow | ≥20 green
  if (percent <= 12) return RED;
  if (percent < 20) return YELLOW;
  return GREEN;
};

// ─── Currency ────────────────────────────────────────────────────────────────

const CURRENCY_SYMBOLS: [string, string][] = [
  ["_usd", "$"],
  ["_gbp", "£"],
  ["_eur", "€"],
  ["_brl", "R$"],
  ["_cad", "C$"],
  ["_ron", "lei"],
  ["_ils", "₪"],
];

// NOTE: Custom overrides added to return "$" symbol for EPC, EPC CPA, Net Rev, PV, CPA PV,
// and RS Income fields, as these requirements are not specified in the default schema documentation.
export const getCurrencySymbol = (key: string): string | null => {
  const lower = key.toLowerCase();

  // Override to return "$" for EPC, EPC CPA, Net Rev, PV, CPA PV, and RS Income fields in Biz Dev Dashboard
  const dollarKeys = new Set([
    "epc",
    "epc_cpa",
    "net_rev",
    "cpa_pv",
    "epc_rs",
    "pv",
    "non_negative_rs_income",
    "rs_income",
  ]);

  if (dollarKeys.has(lower)) {
    return "$";
  }

  for (const [substr, symbol] of CURRENCY_SYMBOLS) {
    if (lower.includes(substr)) return symbol;
  }
  return null;
};

// ─── Formatting ──────────────────────────────────────────────────────────────

export const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "N/A";
  const num = typeof value === "number" ? value : parseFloat(String(value));
  if (!isNaN(num)) {
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  }
  return String(value);
};

// ─── Column building ─────────────────────────────────────────────────────────

export interface OutputMetaField {
  title: string;
  type: string;
  description: string;
  order: string | number;
}

export interface TableColumn {
  key: string;
  title: string;
  order: number;
  hasPerc: boolean;
  isPercentageValue?: boolean;
}

// Keys whose _perc variant is displayed as a standalone column (not merged into the value column)
const SPLIT_PERC_COLUMNS = new Set(["ftd_incent"]);

// Keys whose raw API value is already in 0–100 percentage form (show badge, no ×100)
export const NATIVE_PERCENTAGE_KEYS = new Set(["ftd_incent"]);

// Keys whose raw API value is a 0–1 decimal that should be rendered as a badge (×100)
// Includes both: NATIVE_PERCENTAGE_KEYS (no multiply) and decimal perc keys (multiply)
const PERCENTAGE_VALUE_KEYS = new Set(["ftd_incent", "ftd_incent_perc"]);

const COLUMN_TITLE_OVERRIDES: Record<string, string> = {
  ftd_incent: "QFTD Incent",
  ftd_incent_perc: "INCENT & TIER 5",
};

export const buildColumns = (
  outputMetadata: Record<string, OutputMetaField>,
  isMtd = false,
): TableColumn[] => {
  const metaKeys = Object.keys(outputMetadata);

  // _perc keys that should NOT be merged — they appear as standalone columns
  const percKeys = new Set(
    metaKeys.filter((k) => {
      if (!k.endsWith(PERC_SUFFIX)) return false;
      const base = k.slice(0, -PERC_SUFFIX.length);
      return !SPLIT_PERC_COLUMNS.has(base);
    }),
  );

  return metaKeys
    .filter((key) => !percKeys.has(key))
    .filter((key) => isMtd || !MTD_ONLY_KEYS.has(key))
    .map((key) => {
      const hasPerc = percKeys.has(key + PERC_SUFFIX);
      const isPercentageValue = PERCENTAGE_VALUE_KEYS.has(key);
      const rawTitle =
        COLUMN_TITLE_OVERRIDES[key] ??
        outputMetadata[key].title.replace(/_/g, " ").toUpperCase();
      const title = hasPerc ? `${rawTitle} (%${rawTitle})` : rawTitle;
      return {
        key,
        title,
        order: Number(outputMetadata[key].order),
        hasPerc,
        isPercentageValue,
      };
    })
    .sort((a, b) => a.order - b.order);
};

// ─── Sorting ─────────────────────────────────────────────────────────────────

export const sortRows = (
  rows: Record<string, any>[],
  sortKey: string,
  sortDir: "asc" | "desc",
): Record<string, any>[] => {
  if (!sortKey) return rows;
  return [...rows].sort((a, b) => {
    const aVal = a[sortKey] ?? "";
    const bVal = b[sortKey] ?? "";
    if (aVal === bVal) return 0;
    const cmp = aVal < bVal ? -1 : 1;
    return sortDir === "asc" ? cmp : -cmp;
  });
};

// ─── Diff calculation ────────────────────────────────────────────────────────

export const calcDiffPercent = (primary: any, comp: any): number | null => {
  const a = parseFloat(String(primary ?? "").replace(/[, ]/g, ""));
  const b = parseFloat(String(comp ?? "").replace(/[, ]/g, ""));
  if (isNaN(a) || isNaN(b) || b === 0) return null;
  return ((a - b) / Math.abs(b)) * 100;
};

// ─── CSV Export ───────────────────────────────────────────────────────────────

const escapeCsvCell = (value: string): string => {
  // Wrap in quotes if the value contains a comma, quote, or newline
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export interface ExportOptions {
  columns: TableColumn[];
  primaryRows: Record<string, any>[];
  comparisonRows: Record<string, any>[];
  primaryLabel: string;
  comparisonLabel: string;
  isMtd: boolean;
  filename?: string;
}

export const exportToCSV = ({
  columns,
  primaryRows,
  comparisonRows,
  primaryLabel,
  comparisonLabel,
  filename = "performance_export",
}: ExportOptions): void => {
  const hasComparison = comparisonRows.length > 0;

  // ── Build header row ──────────────────────────────────────────────────────
  const headers: string[] = [];

  columns.forEach((col) => {
    const baseTitle = col.title
      // Strip the " (%TITLE)" suffix that buildColumns appends for perc columns
      .replace(/\s+\(%[^)]+\)$/, "")
      .trim();

    headers.push(`${baseTitle} [${primaryLabel}]`);

    if (col.hasPerc) {
      headers.push(`${baseTitle} % [${primaryLabel}]`);
    }

    if (hasComparison && !NO_COMPARISON_KEYS.has(col.key)) {
      headers.push(`${baseTitle} [${comparisonLabel}]`);

      if (col.hasPerc) {
        headers.push(`${baseTitle} % [${comparisonLabel}]`);
      }

      headers.push(`${baseTitle} Diff %`);
    }
  });

  // ── Build data rows ───────────────────────────────────────────────────────
  const dataRows: string[][] = primaryRows.map((row) => {
    const compRow = hasComparison
      ? comparisonRows.find((r) => r.brand_id === row.brand_id)
      : undefined;

    const cells: string[] = [];

    columns.forEach((col) => {
      const currencySymbol = getCurrencySymbol(col.key);
      const isRoi = col.key.toLowerCase() === "roi";
      const isNativePerc = NATIVE_PERCENTAGE_KEYS.has(col.key);

      const fmtCell = (v: any): string => {
        const str = formatValue(v);
        if (str === "N/A") return str;
        if (isRoi || isNativePerc) return `${str}%`;
        return currencySymbol ? `${currencySymbol}${str}` : str;
      };

      // Primary value
      if (col.isPercentageValue) {
        const raw = row[col.key];
        const pct = NATIVE_PERCENTAGE_KEYS.has(col.key)
          ? Number(raw).toFixed(1)
          : (Number(raw) * 100).toFixed(1);
        cells.push(raw != null ? `${pct}%` : "N/A");
      } else {
        cells.push(fmtCell(row[col.key]));
      }

      // Primary _perc value (combined columns only)
      if (col.hasPerc) {
        const percRaw = row[col.key + PERC_SUFFIX];
        const percStr =
          percRaw != null ? `${(Number(percRaw) * 100).toFixed(1)}%` : "N/A";
        cells.push(percStr);
      }

      if (hasComparison && !NO_COMPARISON_KEYS.has(col.key)) {
        // Comparison value
        if (col.isPercentageValue) {
          const compRaw = compRow?.[col.key];
          const pct =
            compRaw != null
              ? NATIVE_PERCENTAGE_KEYS.has(col.key)
                ? Number(compRaw).toFixed(1)
                : (Number(compRaw) * 100).toFixed(1)
              : null;
          cells.push(pct != null ? `${pct}%` : compRow ? "N/A" : "No Data");
        } else {
          cells.push(compRow ? fmtCell(compRow[col.key]) : "No Data");
        }

        // Comparison _perc value (combined columns only)
        if (col.hasPerc) {
          const compPercRaw = compRow?.[col.key + PERC_SUFFIX];
          const compPercStr =
            compPercRaw != null
              ? `${(Number(compPercRaw) * 100).toFixed(1)}%`
              : compRow
                ? "N/A"
                : "No Data";
          cells.push(compPercStr);
        }

        // Diff %
        const diff = calcDiffPercent(row[col.key], compRow?.[col.key]);
        cells.push(
          diff !== null ? `${diff >= 0 ? "+" : ""}${diff.toFixed(2)}%` : "N/A",
        );
      }
    });

    return cells;
  });

  // ── Serialize to CSV string ───────────────────────────────────────────────
  const csvLines: string[] = [
    headers.map(escapeCsvCell).join(","),
    ...dataRows.map((row) => row.map(escapeCsvCell).join(",")),
  ];
  const csvContent = csvLines.join("\n");

  // ── Trigger browser download ──────────────────────────────────────────────
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
